import React, { FC } from 'react';
import { useTranslation } from 'react-i18next';
import { useInstance } from 'react-ioc';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { LeftOutlined } from '@ant-design/icons';
import { Button, Form, Input } from 'antd';
import LayoutSmall from 'layouts/LayoutSmall';

import { useResetPasswordMutation } from '../../generated/graphql';
import { Store } from '../../model/store/Store';
import { INDEX } from '../../routes';
import { pwdRegex } from '../../shared/regex';

export const ResetPassword: FC = () => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const store = useInstance(Store);
    const [searchParams] = useSearchParams();

    const [resetPassword, resetPasswordData] = useResetPasswordMutation();

    const onSubmit = async (data: any) => {
        await resetPassword({
            variables: {
                code: searchParams.get('code')!,
                password: data.password,
                passwordConfirmation: data.passwordConfirmation,
            },
            onCompleted: (data) => {
                if (data.resetPassword?.jwt) {
                    const jwt = data?.resetPassword.jwt;
                    const userId = data?.resetPassword.user.id;
                    store.auth.setTokenAndId(jwt, userId);
                    store.domain.clearState();
                    navigate(INDEX);
                }
            },
        });
    };

    return (
        <LayoutSmall title={t('Reset password')}>
            <Form layout="vertical" onFinish={onSubmit} scrollToFirstError disabled={resetPasswordData.loading}>
                <Form.Item
                    name="password"
                    label={t('New password')}
                    rules={[
                        { required: true, message: t('Required')! },
                        { min: 6, message: `${t('Min 6 symbols')}` },
                        { pattern: pwdRegex, message: t('Only latin letters and digits')! },
                    ]}
                >
                    <Input.Password />
                </Form.Item>
                <Form.Item
                    name="passwordConfirmation"
                    label={t('Confirm password')}
                    dependencies={['password']}
                    validateFirst={true}
                    rules={[
                        { required: true, message: t('Required')! },
                        { min: 6, message: `${t('Min 6 symbols')}` },
                        { pattern: pwdRegex, message: t('Only latin letters and digits')! },
                        ({ getFieldValue }) => ({
                            validator(_, value) {
                                if (!value || getFieldValue('password') === value) {
                                    return Promise.resolve();
                                }
                                return Promise.reject(new Error(t('Passwords do not match')!));
                            },
                        }),
                    ]}
                >
                    <Input.Password />
                </Form.Item>

                <Form.Item>
                    <Button loading={resetPasswordData.loading} block htmlType={'submit'} type="primary">
                        {t('Reset password')}
                    </Button>
                </Form.Item>

                <Button block type={'link'} onClick={() => navigate(INDEX)} icon={<LeftOutlined />}>
                    {t('Back')}
                </Button>
            </Form>
        </LayoutSmall>
    );
};
